import { errorMessage } from "@api/errors";
import { fieldBooksPreview, useFieldBooksExport, useFieldBooksPresets } from "@api/generated/field-books/field-books";
import { useMeasurementsHistoryPresets } from "@api/generated/measurements/measurements";
import type {
  BlocksIO,
  BookExportInFormat,
  BookPreviewIn,
  FieldBookDraft,
  FieldBookTemplate,
  FieldInfo,
  PresetsOut,
  SeedIO,
} from "@api/generated/model";
import { useNumberingTrials } from "@api/generated/numbering/numbering";
import { useUserMe } from "@api/generated/user/user";
import { useTrial } from "@app/trials";
import { hasRole, useAutosave, useSaveSetting, useSeasonFromLink, useSettings } from "@app/workspace";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { ColumnList, PagePreview, TemplateMenu, TrialChips, useBookFields } from "@features/trials/FieldBookParts";
import {
  applied,
  type BookKind,
  type BookSource,
  type BookState,
  hasSource,
  isList,
  parseReplications,
  SEED,
  specOf,
  stateOf,
  templateOf,
  usesBlocks,
  withKind,
  withTemplates,
} from "@features/trials/fieldBooks";
import { num } from "@ui/format";
import "@features/trials/fieldBooks.css";
import { useWatchedJob, WatchedJobProgress } from "@features/system/jobs";
import { ActionIcon, Button, Checkbox, Loader, NumberInput, SegmentedControl, Select, Textarea, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatNumber, shortTime } from "@ui/format";
import { OptionCards } from "@ui/OptionCards";
import { PageHeader } from "@ui/PageHeader";
import { Splitter } from "@ui/Splitter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const NO_FIELDS: FieldInfo[] = [];

export const Route = createFileRoute("/season/field-books")({
  validateSearch: z.object({ ev: z.number().int().optional() }),
  component: FieldBooksPage,
});

/** Tenyészidőszak → Naplók és vetési listák (the legacy Field Book form): napló A, FB, TA, the general book and the
 *  sowing lists (with seed weight, with seeder blocks) of chosen trials or of a saved query's plots in the query's
 *  order, previewed page by page and made as an .xlsx or PDF file in the background. The setup is kept with the user's
 *  settings; templates save it under a name (and go to a colleague as a file). */
function FieldBooksPage() {
  const season = useSeasonFromLink(Route.useSearch().ev);
  const settings = useSettings();
  const presets = useFieldBooksPresets({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  if (!settings || !season || !presets) return <Loader size="sm" m="md" />;
  return (
    <FieldBooks
      key={season}
      season={season}
      presets={presets}
      stored={settings.field_book_draft ?? null}
      templates={settings.field_book_templates}
    />
  );
}

function FieldBooks(props: { season: number; presets: PresetsOut; stored: FieldBookDraft | null; templates: FieldBookTemplate[] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const saveSetting = useSaveSetting();
  const { presets, season } = props;
  const [init] = useState(() => (props.stored?.ev === season || props.stored?.ev === season + 1 ? props.stored : null));
  const [ev, setEv] = useState(() => init?.ev ?? season + 1); // books are printed for the season about to be sown
  const [state, setState] = useState<BookState>(() => stateOf(presets, init?.spec));
  const [template, setTemplate] = useState<string | null>(null);
  const draft = useMemo<FieldBookDraft>(() => ({ ev, spec: state as never }), [ev, state]);
  const { savedAt } = useAutosave("field_book_draft", draft, init);
  const patch = (p: Partial<BookState>) => setState((s) => ({ ...s, ...p }));
  const page = (p: Partial<BookState["page"]>) => setState((s) => ({ ...s, page: { ...s.page, ...p } }));

  const { trials } = useTrial(ev, undefined, undefined);
  const [zoomed, setZoomed] = useState(false);
  const catalog = useMeasurementsHistoryPresets({ query: { staleTime: Number.POSITIVE_INFINITY } }).data?.fields;
  const fields = useBookFields(presets.built_in, catalog ?? NO_FIELDS, presets.line_fields);
  const querying = state.source === "query";
  // saving the query in the query builder refreshes the preview
  const saved = useSavedQuery(state.query, querying);

  const [pageNo, setPageNo] = useState(1);
  const spec = useMemo(() => specOf(ev, state, presets), [ev, state, presets]);
  const [asked] = useDebouncedValue(
    useMemo(() => ({ ...spec, page_no: pageNo }), [spec, pageNo]),
    400,
  ); // one request per pause
  const previewQuery = (body: BookPreviewIn) => ({
    queryKey: ["field-book-preview", body, body.query != null ? saved.stamp : null],
    queryFn: ({ signal }: { signal: AbortSignal }) => fieldBooksPreview(body, { signal }),
  });
  const ready = hasSource(state);
  const askedReady = querying ? asked.query != null : !!asked.trials?.length; // the debounced request has it too
  const preview = useQuery({
    ...previewQuery(asked),
    enabled: ready && askedReady && saved.ready,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const shown = ready ? preview.data : undefined;
  const pages = shown?.pages ?? 0;
  useEffect(() => {
    // the next page is usually the next one asked for
    if (preview.data && asked.page_no < preview.data.pages)
      void client.prefetchQuery(previewQuery({ ...asked, page_no: asked.page_no + 1 }));
  });

  const watched = useWatchedJob();
  const { watch } = watched;
  const exporter = useFieldBooksExport({ mutation: { onSuccess: (out) => watch(out.job_id) } });
  const make = (format: BookExportInFormat) => exporter.mutate({ data: { ...spec, format } });

  const kinds = presets.kinds.map((k) => ({
    value: k.kind,
    title: t(`fieldBooks.types.${k.kind}.title`),
    sub: t(`fieldBooks.types.${k.kind}.help`),
  }));
  const list = isList(presets, state.kind);
  const defaults = presets.kinds.find((k) => k.kind === state.kind)?.page ?? state.page;
  const seedShown = list && state.columns.some((c) => c.field === SEED);
  const saveTemplates = (next: FieldBookTemplate[]) => saveSetting("field_book_templates", next);
  const summary = shown?.query;
  // plots still on temporary numbers print as 9000xx: the trials printed that the plot numbering has not reached yet
  // (with a query: the trials its plots are in, as the preview found them)
  const printed = querying ? (summary?.trials ?? []) : state.trials;
  const canNumber = hasRole(useUserMe().data?.roles, "nemesito"); // the numbering's own role reads its state
  const numbering = useNumberingTrials({ ev }, { query: { staleTime: 60_000, enabled: canNumber && printed.length > 0 } }).data ?? [];
  const unnumbered = numbering.filter((x) => x.temporary > 0 && printed.includes(x.kis));

  return (
    <div className="page fill">
      <PageHeader
        title={t("fieldBooks.title")}
        season
        subtitle={t("fieldBooks.subtitle")}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            {savedAt && <span className="sm mu">{t("fieldBooks.draftSaved", { time: shortTime(savedAt.toISOString()) })}</span>}
            <TemplateMenu
              templates={props.templates}
              current={template}
              onApply={(x) => {
                setState((s) => applied(presets, s, x));
                setTemplate(x.name);
              }}
              onSave={(name) => {
                saveTemplates(withTemplates(props.templates, [templateOf(name, state)]));
                setTemplate(name.trim());
              }}
              onDelete={(name) => saveTemplates(props.templates.filter((x) => x.name !== name))}
              onImport={(picked) => saveTemplates(withTemplates(props.templates, picked))}
            />
          </div>
        }
      />
      <OptionCards
        label={t("fieldBooks.documentType")}
        options={kinds}
        value={state.kind}
        onChange={(k: BookKind) => {
          setState((s) => withKind(presets, s, k));
          setTemplate(null);
          setPageNo(1);
        }}
      />
      <div className="fill-row">
        <section className="card pad stack fb-side">
          <div className="hstack" style={{ gap: 10, alignItems: "flex-end" }}>
            <SegmentedControl
              size="xs"
              aria-label={t("fieldBooks.year")}
              value={String(ev)}
              onChange={(v) => {
                setEv(Number(v));
                patch({ trials: [] });
                setPageNo(1);
              }}
              data={[season, season + 1].map((y) => ({ value: String(y), label: String(y) }))}
            />
            <span className="sm mu">{t("fieldBooks.yearHelp")}</span>
          </div>
          <SegmentedControl
            size="xs"
            aria-label={t("fieldBooks.source")}
            value={state.source}
            onChange={(v) => {
              patch({ source: v as BookSource });
              setPageNo(1);
            }}
            data={[
              { value: "trials", label: t("fieldBooks.sourceTrials") },
              { value: "query", label: t("queries.source.label") },
            ]}
          />
          {querying && (
            <SavedQuerySelect
              value={state.query}
              onChange={(id) => {
                patch({ query: id });
                setPageNo(1);
              }}
              description={t("fieldBooks.queryHelp")}
              required
            />
          )}
          <div>
            <span className="sec">{t(querying ? "fieldBooks.narrowToTrials" : "fieldBooks.trials")}</span>
            <TrialChips trials={trials} value={state.trials} onChange={(v) => patch({ trials: v })} />
          </div>
          <div className="fb-grid2">
            <TextInput
              size="xs"
              label={t("fieldBooks.header")}
              placeholder={t(`fieldBooks.defaultHeader.${list && state.kind !== "general" ? "list" : "trial"}`)}
              maxLength={120}
              value={state.page.header ?? ""}
              onChange={(e) => page({ header: e.currentTarget.value })}
            />
            <TextInput
              size="xs"
              label={t("fieldBooks.footer")}
              placeholder={t("fieldBooks.defaultFooter", { year: ev })}
              maxLength={120}
              value={state.page.footer ?? ""}
              onChange={(e) => page({ footer: e.currentTarget.value })}
            />
          </div>
          {state.kind === "general" && <span className="sm mu">{t("fieldBooks.repPlaceholderHelp")}</span>}
          {seedShown && <SeedFields state={state} defaults={presets.seed} onChange={(seed) => patch({ seed })} />}
          {usesBlocks(state) && <BlockFields state={state} defaults={presets.blocks} onChange={patch} />}
          {state.kind === "general" && <GeneralFields state={state} onChange={patch} />}
          {list && (
            <ColumnList
              columns={state.columns}
              fields={fields}
              general={state.kind === "general"}
              onChange={(columns) => patch({ columns })}
            />
          )}
          <div className="fb-grid3">
            <NumberInput
              size="xs"
              label={t("fieldBooks.rowsPerPage")}
              min={5}
              max={200}
              allowDecimal={false}
              value={state.page.rows_per_page}
              onChange={(v) => page({ rows_per_page: num(v) ?? defaults.rows_per_page })}
            />
            <NumberInput
              size="xs"
              label={t("fieldBooks.thickLine")}
              title={t("fieldBooks.thickLineHelp")}
              min={0}
              max={200}
              allowDecimal={false}
              value={state.page.bold_every}
              onChange={(v) => page({ bold_every: num(v) ?? defaults.bold_every })}
            />
            <Select
              size="xs"
              label={t("fieldBooks.orientation")}
              allowDeselect={false}
              value={state.page.landscape ? "l" : "p"}
              onChange={(v) => page({ landscape: v === "l" })}
              data={[
                { value: "p", label: t("fieldBooks.portrait") },
                { value: "l", label: t("fieldBooks.landscape") },
              ]}
            />
            <NumberInput
              size="xs"
              label={t("fieldBooks.firstPageNumber")}
              min={1}
              allowDecimal={false}
              value={state.page.first_page}
              onChange={(v) => page({ first_page: num(v) ?? defaults.first_page })}
            />
            {(state.kind === "a" || state.kind === "ta") && (
              <NumberInput
                size="xs"
                label={t("fieldBooks.firstRow")}
                title={t("fieldBooks.firstRowHelp")}
                min={1}
                allowDecimal={false}
                value={state.page.first_subplot}
                onChange={(v) => page({ first_subplot: num(v) ?? defaults.first_subplot })}
              />
            )}
            {list && (
              <Checkbox
                size="xs"
                mt={22}
                label={t("fieldBooks.groupNumbering")}
                checked={state.numbering}
                onChange={(e) => patch({ numbering: e.currentTarget.checked })}
              />
            )}
          </div>
        </section>

        <Splitter cssVar="--fb-side-w" side="start" min={280} max={640} />
        <section className="grow stack fb-preview-col">
          {/* the buttons keep their words: on a narrow preview they go under the title instead of being cut */}
          <div className="hstack" style={{ gap: 8, flexWrap: "wrap" }}>
            <h2 className="h2 grow">
              {t("fieldBooks.printPreview")}
              {pages > 0 && ` · ${t("fieldBooks.pageOf", { n: Math.min(pageNo, pages), count: pages })}`}
              {shown && <span className="sm mu"> · {t("fieldBooks.plotCount", { count: formatNumber(shown.plots) })}</span>}
              {preview.isFetching && <Loader size={10} ml={6} />}
            </h2>
            <div className="hstack" style={{ gap: 8, flex: "0 0 auto" }}>
              <ActionIcon
                variant="subtle"
                aria-label={t("fieldBooks.previousPage")}
                disabled={pageNo <= 1}
                onClick={() => setPageNo((n) => Math.max(1, Math.min(n, pages) - 1))}
              >
                ‹
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                aria-label={t("fieldBooks.nextPage")}
                disabled={pageNo >= pages}
                onClick={() => setPageNo((n) => Math.min(pages, n + 1))}
              >
                ›
              </ActionIcon>
              <Button
                size="compact-sm"
                variant={zoomed ? "light" : "subtle"}
                aria-pressed={zoomed}
                disabled={!shown?.page}
                onClick={() => setZoomed(!zoomed)}
              >
                {t(zoomed ? "fieldBooks.fitPage" : "fieldBooks.zoomIn")}
              </Button>
              <Button size="compact-sm" variant="default" disabled={!pages} loading={exporter.isPending} onClick={() => make("xlsx")}>
                {t("fieldBooks.excel")}
              </Button>
              {list && (
                <Button
                  size="compact-sm"
                  variant="default"
                  title={t("fieldBooks.excelTableHelp")}
                  disabled={!pages}
                  loading={exporter.isPending}
                  onClick={() => make("table")}
                >
                  {t("fieldBooks.excelTable")}
                </Button>
              )}
              <Button size="compact-sm" disabled={!pages} loading={exporter.isPending} onClick={() => make("pdf")}>
                {t("fieldBooks.downloadPdf")}
              </Button>
            </div>
          </div>
          <WatchedJobProgress {...watched} />
          {!!shown?.notes.length && <p className="note warn sm">{t("fieldBooks.missingPlots", { list: shown.notes.join("; ") })}</p>}
          {summary && (
            <p className={summary.unregistered ? "note warn sm" : "sm mu"}>
              {t("fieldBooks.querySummary", { rows: formatNumber(summary.rows), count: formatNumber(summary.plots) })}
              {summary.unregistered > 0 && ` ${t("fieldBooks.queryMissing", { count: formatNumber(summary.unregistered) })}`}
            </p>
          )}
          {preview.isError && <p className="note warn sm">{errorMessage(preview.error)}</p>}
          {unnumbered.length > 0 && (
            <p className="note warn sm">
              {t("fieldBooks.unnumbered", { list: unnumbered.map((x) => `${x.kis} (${x.temporary})`).join(", ") })}{" "}
              <Link to="/season/plot-numbering">{t("nav.season.plotNumbering")}</Link>
            </p>
          )}
          <div className={zoomed ? "fb-preview zoomed" : "fb-preview"}>
            {shown?.page ? (
              <PagePreview page={shown.page} landscape={shown.landscape} footer={shown.footer} />
            ) : (
              <p className="sm mu fb-empty">
                {ready ? t(querying ? "fieldBooks.noPlotsInQuery" : "fieldBooks.noPlots") : t("fieldBooks.chooseSource")}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function SeedFields({ state, defaults, onChange }: { state: BookState; defaults: SeedIO; onChange: (seed: BookState["seed"]) => void }) {
  const { t } = useTranslation();
  const s = state.seed;
  const set = (p: Partial<BookState["seed"]>) => onChange({ ...s, ...p });
  return (
    <div>
      <span className="sec">{t("fieldBooks.seedCalculation")}</span>
      <div className="fb-grid4">
        <NumberInput size="xs" label="m²" min={0.1} value={s.area} onChange={(v) => set({ area: num(v) ?? defaults.area })} />
        <NumberInput
          size="xs"
          label={t("fieldBooks.germsPerM2")}
          min={1}
          allowDecimal={false}
          value={s.density}
          onChange={(v) => set({ density: num(v) ?? defaults.density })}
        />
        <NumberInput
          size="xs"
          label={t("fieldBooks.defaultTkw")}
          min={1}
          value={s.tkw}
          onChange={(v) => set({ tkw: num(v) ?? defaults.tkw })}
        />
        <NumberInput
          size="xs"
          label={t("fieldBooks.defaultGermination")}
          min={1}
          max={100}
          value={s.germ}
          onChange={(v) => set({ germ: num(v) ?? defaults.germ })}
        />
      </div>
      <div className="hstack" style={{ gap: 8, marginTop: 6, alignItems: "flex-end" }}>
        <TextInput
          size="xs"
          w={110}
          style={{ flexShrink: 0 }}
          label={t("fieldBooks.tkwSite")}
          maxLength={10}
          value={s.site}
          onChange={(e) => set({ site: e.currentTarget.value.trim().toUpperCase() })}
        />
        <span className="sm mu">{t("fieldBooks.seedHelp", { th: s.site })}</span>
      </div>
    </div>
  );
}

function BlockFields({ state, defaults, onChange }: { state: BookState; defaults: BlocksIO; onChange: (p: Partial<BookState>) => void }) {
  const { t } = useTranslation();
  const b = state.blocks;
  const set = (p: Partial<BookState["blocks"]>) => onChange({ blocks: { ...b, ...p } });
  return (
    <div>
      <span className="sec">{t("fieldBooks.blocks")}</span>
      <div className="fb-grid3">
        <NumberInput
          size="xs"
          label={t("fieldBooks.blockSize")}
          min={1}
          allowDecimal={false}
          value={b.size}
          onChange={(v) => set({ size: num(v) ?? defaults.size })}
        />
        <NumberInput
          size="xs"
          label={t("fieldBooks.currentBlock")}
          min={1}
          allowDecimal={false}
          value={b.block}
          onChange={(v) => set({ block: num(v) ?? defaults.block })}
        />
        <NumberInput
          size="xs"
          label={t("fieldBooks.startPlot")}
          min={0}
          allowDecimal={false}
          value={state.from_plot ?? ""}
          onChange={(v) => onChange({ from_plot: num(v) })}
        />
        <NumberInput
          size="xs"
          label={t("fieldBooks.blockUsed")}
          min={0}
          allowDecimal={false}
          value={b.used}
          onChange={(v) => set({ used: num(v) ?? defaults.used })}
        />
        <NumberInput
          size="xs"
          label={t("fieldBooks.firstPlotUsed")}
          min={0}
          allowDecimal={false}
          value={b.used_plot}
          onChange={(v) => set({ used_plot: num(v) ?? defaults.used_plot })}
        />
      </div>
      <span className="sm mu">{t("fieldBooks.blocksHelp")}</span>
    </div>
  );
}

function GeneralFields({ state, onChange }: { state: BookState; onChange: (p: Partial<BookState>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => state.replications.map((r) => [r.name, ...(r.plots ?? [])].join("\t")).join("\n"));
  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="sec">{t("fieldBooks.general")}</span>
      <div className="fb-grid2">
        <Checkbox
          size="xs"
          label={t("fieldBooks.rowPerSubplot")}
          checked={state.subplots}
          onChange={(e) => onChange({ subplots: e.currentTarget.checked })}
        />
        <Checkbox
          size="xs"
          label={t("fieldBooks.splitIntoBlocks")}
          checked={state.general_blocks}
          onChange={(e) => onChange({ general_blocks: e.currentTarget.checked })}
        />
        <Checkbox
          size="xs"
          label={t("fieldBooks.sameTrialOnly")}
          checked={state.same_trial}
          onChange={(e) => onChange({ same_trial: e.currentTarget.checked })}
        />
        <Checkbox
          size="xs"
          label={t("fieldBooks.extendedHeader")}
          description={t("fieldBooks.extendedHeaderHelp")}
          checked={state.extended_header}
          onChange={(e) => onChange({ extended_header: e.currentTarget.checked })}
        />
        <Select
          size="xs"
          label={t("fieldBooks.historyPath")}
          allowDeselect={false}
          value={state.history}
          onChange={(v) => v && onChange({ history: v as BookState["history"] })}
          data={[
            { value: "evi", label: t("fieldBooks.historyPathEvi") },
            { value: "e", label: t("fieldBooks.historyPathE") },
            { value: "e_alp", label: t("fieldBooks.historyPathEAlp") },
          ]}
        />
      </div>
      {/* a replication orders a whole trial, a query only its part of it: the query's order is kept */}
      {state.source === "query" ? (
        <p className="sm mu">{t("fieldBooks.repetitionsQueryNote")}</p>
      ) : (
        <>
          <Textarea
            size="xs"
            label={t("fieldBooks.repetitions")}
            description={t("fieldBooks.repetitionsHelp")}
            autosize
            minRows={2}
            maxRows={6}
            value={text}
            onChange={(e) => {
              setText(e.currentTarget.value);
              onChange({ replications: parseReplications(e.currentTarget.value) });
            }}
            styles={{ input: { fontFamily: "var(--b-mono)" } }}
          />
          {state.replications.length > 0 && (
            <Checkbox
              size="xs"
              label={t("fieldBooks.firstRepetitionRandom")}
              checked={state.first_random}
              onChange={(e) => onChange({ first_random: e.currentTarget.checked })}
            />
          )}
        </>
      )}
    </div>
  );
}
